import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { solicitudesAPI } from '../../lib/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { estadoColor, estadoLabel } from './SolicitudesDashboard';
import { toast } from 'sonner';
import {
    CheckCircle2, XCircle, Clock, FileText, Download, ArrowLeft, QrCode, User, Building2, Calendar, Loader2
} from 'lucide-react';

export default function SolicitudDetallePage() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [sol, setSol] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tipo, setTipo] = useState(null);

    // Aprobar state
    const [showAprobar, setShowAprobar] = useState(false);
    const [vigenciaHasta, setVigenciaHasta] = useState('');
    const [gerenciaArea, setGerenciaArea] = useState('');
    const [obsAprobar, setObsAprobar] = useState('');
    const [saving, setSaving] = useState(false);

    // Rechazar state
    const [showRechazar, setShowRechazar] = useState(false);
    const [motivoRechazo, setMotivoRechazo] = useState('');
    const [motivoDetalle, setMotivoDetalle] = useState('');

    useEffect(() => {
        solicitudesAPI.detalle(id).then(r => {
            setSol(r.data);
            return solicitudesAPI.getTipo(r.data.tipo_solicitud_id);
        }).then(r => setTipo(r.data)).catch(err => {
            toast.error('Error al cargar solicitud');
        }).finally(() => setLoading(false));
    }, [id]);

    const handleAprobar = async () => {
        if (!vigenciaHasta) return toast.error('Debe indicar vigencia hasta');
        if (tipo?.requiere_gerencia_area && !gerenciaArea) return toast.error('Debe seleccionar Gerencia de Área');
        setSaving(true);
        try {
            await solicitudesAPI.aprobar(id, {
                vigencia_hasta: vigenciaHasta,
                gerencia_area: gerenciaArea || null,
                observaciones: obsAprobar,
            });
            toast.success('Solicitud aprobada');
            const r = await solicitudesAPI.detalle(id);
            setSol(r.data);
            setShowAprobar(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error');
        } finally { setSaving(false); }
    };

    const handleRechazar = async () => {
        if (!motivoRechazo) return toast.error('Debe seleccionar un motivo');
        setSaving(true);
        try {
            await solicitudesAPI.rechazar(id, { motivo: motivoRechazo, motivo_detalle: motivoDetalle });
            toast.success('Solicitud rechazada');
            const r = await solicitudesAPI.detalle(id);
            setSol(r.data);
            setShowRechazar(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error');
        } finally { setSaving(false); }
    };

    if (loading) return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></Layout>;
    if (!sol) return <Layout><div className="text-center py-12 text-muted-foreground">Solicitud no encontrada</div></Layout>;

    const isPIAdmin = ['admin', 'seguridad', 'superadmin'].includes(user?.rol);
    const canResolve = isPIAdmin && ['creada', 'en_verificacion'].includes(sol.estado);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    return (
        <Layout>
        <div className="space-y-4 max-w-4xl mx-auto" data-testid="solicitud-detalle">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2">
                <ArrowLeft className="h-4 w-4 mr-1" /> Volver
            </Button>

            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-xl font-bold">{sol.tipo_nombre}</h1>
                    <p className="text-sm text-muted-foreground">ID: {sol.id}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Badge className={`text-sm px-3 py-1 ${estadoColor[sol.estado]}`}>{estadoLabel[sol.estado]}</Badge>
                    <a href={solicitudesAPI.getQrUrl(sol.id)} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline"><QrCode className="h-4 w-4 mr-1" /> QR</Button>
                    </a>
                    {sol.estado === 'aprobada' && (
                        <a href={solicitudesAPI.getDocumentoPdfUrl(sol.id)} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" data-testid="btn-descargar-pdf">
                                <Download className="h-4 w-4 mr-1" /> Documento PDF
                            </Button>
                        </a>
                    )}
                </div>
            </div>

            {/* Solicitante */}
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><User className="h-4 w-4" /> Solicitante</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div><span className="text-muted-foreground">Nombre:</span> <strong>{sol.solicitante?.nombre}</strong></div>
                    <div><span className="text-muted-foreground">Email:</span> <strong>{sol.solicitante?.email}</strong></div>
                    <div><span className="text-muted-foreground">Empresa:</span> <strong>{sol.solicitante?.empresa}</strong></div>
                </CardContent>
            </Card>

            {/* Datos dinámicos */}
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Datos de la Solicitud</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    {tipo?.campos?.map(campo => (
                        <div key={campo.key} className={campo.tipo === 'textarea' ? 'col-span-full' : ''}>
                            <span className="text-muted-foreground">{campo.label}:</span>{' '}
                            <strong>{sol.datos?.[campo.key] || '—'}</strong>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Documentos */}
            {sol.documentos?.length > 0 && (
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Download className="h-4 w-4" /> Documentos Adjuntos</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {sol.documentos.map((doc, i) => {
                            const docLabel = tipo?.documentos_obligatorios?.find(d => d.key === doc.key)?.label || doc.key;
                            const fileUrl = doc.url.startsWith('http') ? doc.url : `${backendUrl}${doc.url}`;
                            return (
                                <div key={i} className="flex items-center justify-between border rounded-lg p-3">
                                    <div>
                                        <p className="font-medium text-sm">{docLabel}</p>
                                        <p className="text-xs text-muted-foreground">{doc.filename}</p>
                                    </div>
                                    <a href={fileUrl} target="_blank" rel="noreferrer">
                                        <Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1" /> Descargar</Button>
                                    </a>
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>
            )}

            {/* Resolución (si existe) */}
            {sol.resolucion && (
                <Card className={sol.estado === 'aprobada' ? 'border-green-300' : 'border-red-300'}>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            {sol.estado === 'aprobada' ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                            Resolución
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                        {sol.estado === 'aprobada' ? (
                            <>
                                <p><span className="text-muted-foreground">Aprobado por:</span> <strong>{sol.resolucion.aprobado_por}</strong></p>
                                <p><span className="text-muted-foreground">Vigencia desde:</span> <strong>{new Date(sol.resolucion.vigencia_desde).toLocaleDateString('es-CL')}</strong></p>
                                <p><span className="text-muted-foreground">Vigencia hasta:</span> <strong>{sol.resolucion.vigencia_hasta ? new Date(sol.resolucion.vigencia_hasta).toLocaleDateString('es-CL') : '—'}</strong></p>
                                {sol.resolucion.gerencia_area && <p><span className="text-muted-foreground">Gerencia de Área:</span> <strong>{sol.resolucion.gerencia_area}</strong></p>}
                                {sol.resolucion.observaciones && <p><span className="text-muted-foreground">Observaciones:</span> {sol.resolucion.observaciones}</p>}
                            </>
                        ) : (
                            <>
                                <p><span className="text-muted-foreground">Rechazado por:</span> <strong>{sol.resolucion.rechazado_por}</strong></p>
                                <p><span className="text-muted-foreground">Motivo:</span> <strong>{sol.resolucion.motivo}</strong></p>
                                {sol.resolucion.motivo_detalle && <p><span className="text-muted-foreground">Detalle:</span> {sol.resolucion.motivo_detalle}</p>}
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Historial */}
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Historial</CardTitle></CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {sol.historial_estados?.map((h, i) => (
                            <div key={i} className="flex items-center gap-3 text-sm">
                                <Badge className={`text-xs ${estadoColor[h.estado]}`}>{estadoLabel[h.estado]}</Badge>
                                <span className="text-muted-foreground">{new Date(h.fecha).toLocaleString('es-CL')}</span>
                                <span>{h.usuario}</span>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Botones de acción PI */}
            {canResolve && !showAprobar && !showRechazar && (
                <div className="flex gap-3">
                    <Button onClick={() => setShowAprobar(true)} className="bg-green-600 hover:bg-green-700" data-testid="btn-aprobar">
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Aprobar
                    </Button>
                    <Button variant="destructive" onClick={() => setShowRechazar(true)} data-testid="btn-rechazar">
                        <XCircle className="h-4 w-4 mr-1" /> Rechazar
                    </Button>
                </div>
            )}

            {/* Modal Aprobar */}
            {showAprobar && (
                <Card className="border-green-300">
                    <CardHeader><CardTitle className="text-base">Aprobar Solicitud</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <Label>Vigencia hasta *</Label>
                            <Input type="date" value={vigenciaHasta} onChange={e => setVigenciaHasta(e.target.value)} data-testid="vigencia-hasta" />
                        </div>
                        {tipo?.requiere_gerencia_area && (
                            <div>
                                <Label>Gerencia de Área *</Label>
                                <select className="w-full border rounded-md p-2 bg-background" value={gerenciaArea} onChange={e => setGerenciaArea(e.target.value)} data-testid="gerencia-area">
                                    <option value="">Seleccionar...</option>
                                    {tipo.gerencias_area?.map(g => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </div>
                        )}
                        <div>
                            <Label>Observaciones</Label>
                            <Input value={obsAprobar} onChange={e => setObsAprobar(e.target.value)} placeholder="Opcional" />
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={handleAprobar} disabled={saving} className="bg-green-600 hover:bg-green-700">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Confirmar Aprobación
                            </Button>
                            <Button variant="outline" onClick={() => setShowAprobar(false)}>Cancelar</Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Modal Rechazar */}
            {showRechazar && (
                <Card className="border-red-300">
                    <CardHeader><CardTitle className="text-base">Rechazar Solicitud</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <Label>Motivo de Rechazo *</Label>
                            <select className="w-full border rounded-md p-2 bg-background" value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)} data-testid="motivo-rechazo">
                                <option value="">Seleccionar motivo...</option>
                                {tipo?.motivos_rechazo?.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Detalle adicional</Label>
                            <Input value={motivoDetalle} onChange={e => setMotivoDetalle(e.target.value)} placeholder="Opcional" />
                        </div>
                        <div className="flex gap-2">
                            <Button variant="destructive" onClick={handleRechazar} disabled={saving}>
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />} Confirmar Rechazo
                            </Button>
                            <Button variant="outline" onClick={() => setShowRechazar(false)}>Cancelar</Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
        </Layout>
    );
}
